import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentElementsGenericComponent, FaultArticle } from '../modules/graphql/generated/graphql-types';
import { AuthService } from './auth.service';
import { SeoService } from './seo.service';
import { SessionService } from './session.service';
import { WorkflowService } from './workflow.service';
import { ButtonLevel, ButtonStyle, ButtonType } from '@challenger6/vm-pattern-library/button';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IconWeight } from '../components/confirmation/confirmation.model';
import { STRAPI_CONSTANTS } from '../constants/strapi-constants';
import { FaultArticleService } from './fault-article.service';
import { PriorityCallService } from './priority-call.service';

@Injectable()
export class StrapiGenericComponentService {
  articleObj;
  activeJourney;
  strapiID: string;

  constructor(
    private faultArticleService: FaultArticleService,
    private seoService: SeoService,
    private router: Router,
    private workflowService: WorkflowService,
    private sessionService: SessionService,
    private authService: AuthService,
    private priorityCallService: PriorityCallService
  ) {}

  public gotoNextAction(options): void {
    const { nextAction, route, journeyMap, vmSignIn, vmSignedIn } = options;
    if (nextAction?.uid) {
      if (nextAction?.uid === vmSignedIn) {
        this.fetchSessionDataAndUpdateJourney(journeyMap[nextAction?.id], route);
      } else {
        this.workflowService.goToNextReturnCode(nextAction.uid).subscribe((workflow) => {
          if (vmSignIn === workflow?.reasonReturnCode) {
            this.authService.redirectToLogin();
          } else {
            const next = journeyMap[workflow?.externalRef];
            if (next?.includes('https://')) {
              window.location.href = next;
              return;
            } else {
              if (next?.includes('{action_code}')) {
                this.fetchSessionDataAndUpdateJourney(next, route);
              } else {
                this.router.navigate([next], { relativeTo: route });
              }
            }
          }
        });
      }
    }
  }

  public updateActionCodePlaceholder(next: any, actionCode: any) {
    next = next?.replace('{action_code}', actionCode?.toLowerCase());
    return next;
  }

  fetchSessionDataAndUpdateJourney(next: any, route: any) {
    this.sessionService.getSessionData().subscribe((sessionResponse) => {
      const actionCode = sessionResponse?.angularPublicSession?.diagnosticsActionCode?.toLowerCase();
      next = this.updateActionCodePlaceholder(next, actionCode);
      this.router.navigate([next], { relativeTo: route });
    });
  }

  // @ts-ignore
  fetchFormattedContent(options): Observable<any> {
    const strapiUID = this.getStrapiIdFromMapping(options);
    return this.getArticleContent(strapiUID);
  }

  // @ts-ignore
  fetchContent(strapiUID, ivrPriority = false, ivrPriorityIdentified = true): Observable<any> {
    return this.getArticleContent(strapiUID, ivrPriority, ivrPriorityIdentified);
  }

  fetchAdviceContent(strapiUID) {
    return this.faultArticleService.getArticle(strapiUID).pipe(
      map((faultArticle: FaultArticle) => {
        return this.formatAdviceContent(faultArticle);
      })
    );
  }

  public formatAdviceContent(faultArticle: FaultArticle) {
    let modalActions = [
      {
        uid: '',
        url: '',
        label: '',
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
      },
      {
        uid: '',
        url: '',
        label: '',
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
        buttonStyle: ButtonStyle.OUTLINE,
      },
    ];
    this.seoService.updatePageSeo(faultArticle?.titleOfTheBrowser || faultArticle?.articleTemplate?.heading1);
    const items = [];
    let modalLabel = '';
    let modalCopy = '';
    let modalTitle = '';

    const adviceSteps = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement?.type !== 'textAndContentSection');

    const stepModals = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement.type === 'textAndContentSection');

    // @ts-ignore
    adviceSteps.forEach((element: ComponentElementsGenericComponent) => {
      const cta = element?.ctas && element?.ctas?.length > 0 ? element?.ctas[0] : null;
      let modalObj = {};
      if (cta?.Uid) {
        if (cta?.contentUid === 'modal') {
          const modalContent = stepModals.filter((modalElement) => modalElement?.uid === cta?.Uid);
          modalLabel = modalContent[0]?.title;
          modalCopy = modalContent[0]?.copy;
          modalTitle = modalContent[0]?.subtitle;
          modalActions = this.getComponentCTAButtons([modalContent[0]]);
          modalObj = {
            modalLabel,
            modalCopy,
            modalTitle,
            modalActions,
          };
        }
      }

      const item = {
        title: element?.title,
        subtitle: element?.subtitle,
        media: {
          src: element?.icon[0],
          alt: '',
          mediaType: 'image',
          aspectRatio: '16:9',
        },
        description: element?.copy,
        actions: this.getComponentCTAButtons([element]),
        modalObj,
      };
      items.push(item);
    });
    return (this.articleObj = {
      items,
    });
  }

  getListOfLinks(content) {
    let listOfLinks = [];

    if (Array.isArray(content)) {
      for (const item of content) {
        listOfLinks.push({
          modalTitle: item?.subtitle,
          modalContent: item?.copy,
          text: item?.title,
        });
      }
      listOfLinks = listOfLinks.slice(1);
    }
    return listOfLinks;
  }

  private getArticleContent(strapiUID, ivrPriority = false, ivrPriorityIdentified = true) {
    return this.faultArticleService.getArticle(strapiUID).pipe(
      map((faultArticle: FaultArticle) => {
        return this.formatArticleContent(faultArticle, ivrPriority, ivrPriorityIdentified);
      })
    );
  }

  public formatArticleContent(faultArticle: FaultArticle, ivrPriority = false, ivrPriorityIdentified = true) {
    const content = this.getGenericComponentContent(faultArticle);
    const tilesObj = this.getTilesContent(faultArticle, ivrPriority, ivrPriorityIdentified);
    const tiles = tilesObj?.tiles;
    const tilesContainerHeading = tilesObj?.tilesContainerHeading;
    const tilesContainerCopy = tilesObj?.tilesContainerCopy;
    const anchorLinks = this.getAnchorLinks(faultArticle);
    const info = content[0]?.copy;
    const title = content[0]?.title;
    const { iconSrc, icon, iconColor, iconWeight } = this.getIcon(content);
    const listOfLinks = this.getListOfLinks(content);
    const actions = this.getComponentCTAButtons(content, ivrPriority);
    let chatbotId = null;

    for (let cta of actions) {
      if (cta.uid === 'chat') {
        chatbotId = cta?.chatBotId;
      }
    }

    let extraInfo = null;
    if (content[0]?.listOfContent && content[0]?.listOfContent.length > 0) {
      extraInfo = content[0]?.listOfContent[0]?.content;
    }
    const accordionObj = this.getAccordionContent(faultArticle);
    const {
      secondaryModalLabel,
      secondaryModalCopy,
      secondaryModalTitle,
      tertiaryModalLabel,
      tertiaryModalCopy,
      tertiaryModalTitle,
    } = this.getModals(content, ivrPriority, ivrPriorityIdentified);
    const textWithModals = this.getTextWithModal(faultArticle);

    return {
      iconSrc,
      icon,
      iconColor,
      iconWeight,
      title,
      info,
      listOfLinks,
      actions,
      extraInfo,
      secondaryModalLabel,
      secondaryModalCopy,
      tertiaryModalLabel,
      secondaryModalTitle,
      tertiaryModalCopy,
      tertiaryModalTitle,
      tiles,
      tilesContainerHeading,
      tilesContainerCopy,
      content,
      chatbotId,
      anchorLinks,
      textWithModals,
      accordionObj,
    };
  }

  private getModals(content, ivrPriority = false, ivrPriorityIdentified = true) {
    let secondaryModalLabel = '';
    let secondaryModalCopy = '';
    let secondaryModalTitle = '';
    let tertiaryModalLabel = '';
    let tertiaryModalCopy = '';
    let tertiaryModalTitle = '';
    if (content.length > 1) {
      const modalContent = content.slice(1);
      secondaryModalLabel = modalContent[0]?.title;
      secondaryModalCopy = this.priorityCallService.setModalContent(
        secondaryModalLabel,
        modalContent[0]?.copy,
        ivrPriority,
        ivrPriorityIdentified
      );
      secondaryModalTitle = modalContent[0]?.subtitle;
      tertiaryModalLabel = modalContent[1]?.title;
      tertiaryModalCopy = this.priorityCallService.setModalContent(
        tertiaryModalLabel,
        modalContent[1]?.copy,
        ivrPriority,
        ivrPriorityIdentified
      );
      tertiaryModalCopy = modalContent[1]?.copy;
      tertiaryModalTitle = modalContent[1]?.subtitle;
    }
    return {
      secondaryModalLabel,
      secondaryModalCopy,
      secondaryModalTitle,
      tertiaryModalLabel,
      tertiaryModalCopy,
      tertiaryModalTitle,
    };
  }

  private getIcon(content) {
    let iconSrc = '';
    if (content[0]?.icon && content[0]?.icon.length > 0) {
      iconSrc = content[0]?.icon[0];
    }
    const icon = content[0]?.fa_icon_name;
    const iconColor = content[0]?.fa_icon_color;
    const iconWeight: IconWeight = content[0]?.fa_icon_weight
      ? IconWeight[content[0]?.fa_icon_weight?.toUpperCase()]
      : '';
    return { iconSrc, icon, iconColor, iconWeight };
  }

  private getTilesContent(faultArticle: FaultArticle, ivrPriority = false, ivrPriorityIdentified = true) {
    const tileContent = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter(
        (genericElement) =>
          genericElement.type === 'tileWithIconSmallSection' || genericElement.type === 'tileWithIconLargeSection'
      );
    const tileDesc = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement.type === 'textAndContentSection');
    let tilesContainerHeading;
    let tilesContainerCopy;
    if (tileDesc) {
      tilesContainerHeading = tileDesc[0]?.title;
      tilesContainerCopy = tileDesc[0]?.copy;
    }

    const tiles = [];

    tileContent?.forEach((ele) => {
      const type = ele?.uid === 'chat' || ele?.uid === 'modal' ? ele?.uid : 'normal';
      let modalTitle;
      let modalCopy;
      if (type === 'modal') {
        modalTitle = ele?.listOfContent[0]?.text;
        modalCopy = ele?.listOfContent[0]?.content;
      }
      if (ele?.ctas?.length > 0) {
        tiles.push({
          title: ele?.title,
          label: ele?.ctas[0]?.label,
          alt: ele?.ctas[0].alt,
          url: ele?.ctas[0]?.url,
          targetNewPage: ele?.ctas[0]?.targetNewPage,
          iconSrc: ele?.ctas[0]?.icon[0],
          icon: ele?.fa_icon_name,
          iconColor: ele?.fa_icon_color,
          iconWeight: ele?.fa_icon_weight ? IconWeight[ele?.fa_icon_weight?.toUpperCase()] : IconWeight.BRANDS,
        });
      } else {
        tiles.push({
          title: ele?.title,
          info: ele?.copy,
          subtitle: ele?.subtitle,
          iconSrc: ele?.icon[0],
          uid: ele?.uid,
          id: ele?.externalUrl,
          type,
          modalTitle,
          modalCopy,
          icon: ele?.fa_icon_name,
          iconColor: ele?.fa_icon_color,
          iconWeight: ele?.fa_icon_weight ? IconWeight[ele?.fa_icon_weight?.toUpperCase()] : IconWeight.BRANDS,
          iconSize: ele?.type === 'tileWithIconLargeSection' ? 'lg' : 'sm',
        });
      }
    });

    const tilesObj = {
      tiles: this.priorityCallService.setTileContent(tiles, ivrPriority, ivrPriorityIdentified),
      tilesContainerHeading,
      tilesContainerCopy,
    };
    return tilesObj;
  }

  getAccordionContent(faultArticle: FaultArticle) {
    const accordionContent = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement.type === 'accordionSection');
    let accordionContainerHeading;
    if (accordionContent) {
      accordionContainerHeading = accordionContent[0]?.title;
    }

    const accordions = [];
    const accordionItems = accordionContent.slice(1);
    accordionItems?.forEach((ele) => {
      if (ele) {
        accordions.push({
          title: ele?.title,
          copy: ele?.copy,
          icon: ele?.fa_icon_name,
          iconColor: ele?.fa_icon_color,
          iconWeight: ele?.fa_icon_weight ? IconWeight[ele?.fa_icon_weight?.toUpperCase()] : IconWeight.BRANDS,
        });
      }
    });
    const accordionObj = {
      accordions,
      accordionContainerHeading,
    };
    return accordionObj;
  }

  private getComponentCTAButtons(content, ivrPriority = false) {
    const actions = [
      {
        uid: '',
        url: '',
        label: '',
        chatBotId: null,
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
      },
      {
        uid: '',
        url: '',
        label: '',
        chatBotId: null,
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
        buttonStyle: ButtonStyle.OUTLINE,
      },
    ];
    content[0]?.ctas?.forEach((cta, index) => {
      let buttonType;
      let chatBotId;

      switch (cta?.contentUid) {
        case 'modal':
          buttonType = ButtonType.MODAL;
          break;
        case 'link':
          buttonType = ButtonType.LINK;
          break;
        case 'router':
          buttonType = ButtonType.ROUTER_LINK;
          break;
        case 'countdown':
          buttonType = ButtonType.COUNTDOWNCTA;
          break;
        case 'url':
          buttonType = ButtonType.URL;
          break;
        default:
          buttonType = actions[index]?.type;
          break;
      }

      if (cta?.Uid === 'chat') {
        buttonType = ButtonType.CHAT;
        chatBotId = cta.contentUid ? cta.contentUid : null;
      }
      if (cta?.dataAttr === 'outline') {
        actions[index].buttonStyle = ButtonStyle.OUTLINE;
      }

      const label = this.priorityCallService.setButtonLabel(cta?.label, buttonType, ivrPriority);

      actions[index] = {
        ...actions[index],
        type: buttonType,
        label,
        chatBotId,
        uid: cta?.Uid,
        url: cta?.url,
      };
    });

    actions.length = content[0]?.ctas && content[0]?.ctas?.length > 0 ? content[0]?.ctas?.length : 0;
    return actions;
  }

  private getGenericComponentContent(faultArticle: FaultArticle) {
    const content = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement?.type !== 'tileWithIconSmallSection');
    return content;
  }

  private getStrapiIdFromMapping(options) {
    const { params, diagnosticCodesMap, checkServicesCodeMap } = options;
    const codeId = params.get('codeId')?.toUpperCase();
    const codeMappingKey = diagnosticCodesMap.find((entry) => {
      return entry?.value?.includes(codeId);
    })?.key;
    const codePagePath = this.router.url.split('/').slice(-1).pop()?.toUpperCase();
    let strapiUID;
    if (codePagePath.includes('?EMBEDDEDWEBAPP=TRUE')) {
      const codePagePathtest = codePagePath.split('?');
      strapiUID = checkServicesCodeMap[codeMappingKey][codePagePathtest[0]];
    } else {
      strapiUID = checkServicesCodeMap[codeMappingKey][codePagePath];
    }
    return strapiUID;
  }

  private getAnchorLinks(faultArticle: FaultArticle) {
    const anchorLinks = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent?.filter((genericElement) => genericElement.type === 'anchorLinksSection');
    return anchorLinks;
  }
  private getTextWithModal(faultArticle: any) {
    const textWithModals = [];
    const textWithModalContent = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement.type === 'choiceChipSection');
    const modals = (faultArticle as any)?.elements
      ?.filter((element) => element.__typename === 'ComponentElementsGlobalComponent')[0]
      ?.genericComponent.filter((genericElement) => genericElement.type === 'textAndContentSection');

    textWithModalContent?.forEach((ele) => {
      const cta = ele?.ctas[0];
      let modalObj = {};
      if (cta?.Uid) {
        if (cta?.contentUid === 'modal') {
          const modalContent = modals.filter((modalElement) => modalElement?.uid === cta?.Uid);
          const modalLabel = modalContent[0]?.title;
          const modalCopy = modalContent[0]?.copy;
          const modalTitle = modalContent[0]?.subtitle;
          const modalActions = this.getComponentCTAButtons([modalContent[0]]);
          modalObj = {
            modalLabel,
            modalCopy,
            modalTitle,
            modalActions,
          };
        }
      }

      textWithModals.push({
        title: ele?.title,
        info: ele?.copy,
        errorMsg: ele?.subtitle,
        uid: ele?.uid,
        modalDetails: modalObj,
        icon: ele?.fa_icon_name,
        iconColor: ele?.fa_icon_color,
        iconWeight: ele?.fa_icon_weight ? IconWeight[ele?.fa_icon_weight?.toUpperCase()] : IconWeight.BRANDS,
      });
    });

    return textWithModals;
  }
}
